import { DateTime } from 'luxon'
import type { CapacitySet } from '~/models/capacitySets'
import type {
  AutoBookEmptyRequestPreset,
  AutoBookEmptySettings,
  AutoBookRequestPreset,
  CapacitySetRemainingCapacities,
  ExportDatesChangedNotification,
  GateTransaction,
  GETActionMetadata,
  GETAppointment,
  GETAppointmentWithContainer,
  GETAutoBookEmptyRequest,
  GETAutoBookEvaluation,
  GETAutoBookRequest,
  GETAutoBookRequestMinimal,
  GETAutoBookSlotEvaluation,
  GETBaseAppointmentSlot,
  BookingRequest as GETBookingRequest,
  GETBookingStyleRequestWithApptNum,
  GETCameraHistoryImage,
  GETCameraImage,
  CancellationRequest as GETCancellationRequest,
  GETCapacityBucket,
  GETCapacitySet,
  GETConnection,
  GETConnectionMetadata,
  GETContainer,
  GETContainerAvailability,
  GETContainerBasic,
  GETContainerLineAndType,
  GETCurrentTerminalGateScheduleResponse,
  GETEmptyInAppointmentSlot,
  EmptyRescheduleRequest as GETEmptyRescheduleRequest,
  GETEmptyReturnRule,
  GETExport,
  GETExportBookingCheck,
  GETExportLastCheck,
  GETExportStatus,
  GETGateScheduleEntryResponse,
  GETImportStatus,
  GETLineImportStatus,
  GETNote,
  GETOrganization,
  GETPierPass,
  GETPortalMetadata,
  RescheduleRequest as GETRescheduleRequest,
  GETShippingLine,
  GETSlotWindow,
  GETToken,
  GETTruck,
  GETTruckWithStats,
  GETUserInfo,
  GETVesselSchedules,
  GETWatch,
  GlobalNotification,
  LinkedAppointment,
  MatchedVesselForExport,
  ParsedExportEquipmentQuantity,
  SavedCustomer,
  SimpleUser,
  SlotDetails,
  TerminalReadingRules,
  VesselScheduleForExport,
  VesselScheduleHistory,
} from '~/services/apiClient'
import { TERMINAL_NOT_REQUIRING_APPOINTMENTS } from '~/constants'
import { Appointment } from '~/models/appointments'
import {
  AppointmentSlot,
  BaseAppointmentSlot,
  EmptyInAppointmentSlot,
} from '~/models/appointmentSlot'
import { AppointmentWithContainerInfo } from '~/models/appointmentWithContainerInfo'
import BookingRequest from '~/models/bookingRequest'
import { makeCapacitySetFromAPI } from '~/models/capacitySets'
import { Container } from '~/models/containers'
import type { LineLFDGapConfig } from '~/models/lineLFDGap'
import { GateScheduleEntry, TerminalGateSchedule } from '~/models/gateSchedules'
import { ReturnRule } from '~/models/returnRules'
import { UserInfo } from '~/models/userInfo'
import {
  ActionStatus,
  AppointmentStatus,
  AutoBookRequestStatus,
  BotActionName,
  ConnectionCategory,
  ConnectionType,
  ConnectionValidationStatus,
  ContainerType,
  ContainerWatchState,
  DayPreferenceType,
  EmptyDayPreferenceType,
  ExportBookingCheckStatus,
  ExportBookingState,
  ExportWatchState,
  FieldChangeValueType,
  GateStatus,
  GlobalNotificationDisplayType,
  GlobalNotificationLevelEnum,
  ImportLocation,
  LineStatus,
  OldBookingStatus,
  OrganizationSubscription,
  PortalName,
  PortComplex,
  PreferenceOption,
  ReceivingRule,
  Shift,
  ShippingLine,
  TerminalName,
  TransactionDirection,
  UserRoleType,
  VesselScheduleEventType,
  VesselScheduleField,
} from '~/services/apiClient'
import { calculateContainerCheckDigit, fastParseISO } from '~/utils'

// Exported for stories that want a fixed reference date matching the global
// Storybook MOCK_NOW. Factories themselves use DateTime.now() so they respect
// per-story time overrides via Settings.now.
export const FIXTURE_NOW = DateTime.utc(2024, 1, 15, 12, 0, 0)

// Simple seeded PRNG — deterministic "random" values for test data that needs
// variety without flakiness. Each createRandom* call creates its own instance
// so results don't depend on call order across files.
function createSeededRandom(seed: number = 42) {
  let state = seed
  return () => {
    state = (state * 1_664_525 + 1_013_904_223) & 4_294_967_295
    return (state >>> 0) / 4_294_967_296
  }
}

let containerCounter = 100000
const CONTAINER_PREFIXES = [
  'CSNU',
  'TGBU',
  'MEDU',
  'FCIU',
  'YMMU',
  'MAEU',
  'EGLV',
]

export function generateContainerNumber(): string {
  const prefix =
    CONTAINER_PREFIXES[containerCounter % CONTAINER_PREFIXES.length]
  const numbers = containerCounter++
  const baseNumber = `${prefix}${numbers}`
  const checkDigit = calculateContainerCheckDigit(baseNumber)
  return `${baseNumber}${checkDigit}`
}

/** @deprecated Use generateContainerNumber() — deterministic replacement */
export const generateRandomContainerNumber = generateContainerNumber

let bookingCounter = 100000
const BOOKING_CARRIER_PREFIXES = [
  'MAEU',
  'COSU',
  'MSCU',
  'ONEY',
  'HLCU',
  'EGLV',
]

export function generateBookingNumber(): string {
  const prefix =
    BOOKING_CARRIER_PREFIXES[bookingCounter % BOOKING_CARRIER_PREFIXES.length]
  return `${prefix}${bookingCounter++}`
}

export function getRandomTerminal(
  terminals?: TerminalName[],
  rand: () => number = Math.random
): TerminalName {
  const terminalsToUse =
    terminals ??
    Object.values(TerminalName).filter(
      (terminal) => !TERMINAL_NOT_REQUIRING_APPOINTMENTS.has(terminal)
    )
  return terminalsToUse[Math.floor(rand() * terminalsToUse.length)]
}

export function createGETLineImportStatus(
  status?: Partial<GETLineImportStatus>
): GETLineImportStatus {
  return {
    id: 2,
    status: 'in terminal',
    parsed_status: LineStatus.Discharged,
    parsed_shipping_line: ShippingLine.Oney,
    terminal: TerminalName.Apm,
    source: PortalName.ApmTermpointApi,
    container_number: 'CBHU8733881',
    last_free_date: '2021-09-10T00:00:00.000Z',
    observed: '2021-09-27T17:32:39.232453+00:00',
    last_observed: '2021-09-27T23:50:15.320766+00:00',
    ...status,
  }
}

export function createGETImportStatus(
  status?: Partial<GETImportStatus>
): GETImportStatus {
  return {
    id: 2,
    cycle_id: 2,
    terminal: TerminalName.Apm,
    source: PortalName.ApmTermpointApi,
    location: 'in terminal',
    parsed_location: ImportLocation.InTerminal,
    container_number: 'CBHU8733881',
    last_free_date: '2021-09-10T00:00:00.000Z',
    holds: [],
    ready_for_appointment: true,
    raw_data: {},
    extra_data: {},
    observed: '2021-09-27T17:32:39.232453+00:00',
    last_initiated_check: '2021-09-27T23:50:15.320766+00:00',
    last_observed: '2021-09-27T23:50:15.320766+00:00',
    parsed_holds: [],
    ...status,
  }
}

export function createGETAppointment(
  appointment?: Partial<GETAppointment> & { tags?: string[] | null }
): GETAppointment {
  return {
    id: 1,
    container_number: 'CBHU8733881',
    direction: TransactionDirection.Outbound,
    loaded: true,
    status: AppointmentStatus.Scheduled,
    window_start: '2021-09-10T00:00:00.000Z',
    window_end: '2021-09-10T01:00:00.000Z',
    linked_appointments: [],
    terminal: TerminalName.Apm,
    terminal_reference: 'APM123',
    observed: '2021-09-27T17:32:39.232453+00:00',
    source: PortalName.ApmTermpointApi,
    extra_data: {},
    ...appointment,
  }
}
export function createAppointment(
  appointment?: Partial<GETAppointment> & { tags?: string[] | null }
): Appointment {
  return new Appointment(createGETAppointment(appointment), false)
}

export function createGETAppointmentWithContainer(
  appointmentInfo?: Partial<GETAppointmentWithContainer>
): GETAppointmentWithContainer {
  return {
    appointment: createGETAppointment(),
    watch: createGETWatch(),
    ...appointmentInfo,
  }
}

export function createRandomGETAppointmentsWithContainers({
  numAppointments,
  after,
  before,
  direction,
  loaded,
  statuses,
  terminals,
}: {
  numAppointments: number
  after?: DateTime
  before?: DateTime
  direction?: TransactionDirection
  loaded?: boolean
  statuses?: AppointmentStatus[]
  terminals?: TerminalName[]
}): GETAppointmentWithContainer[] {
  const rand = createSeededRandom(numAppointments)
  const now = DateTime.now()
  let appointmentID = 1
  const rangeStart = after ?? now
  const rangeEnd = before ?? now.plus({ days: 5 })
  const rangeHours = rangeEnd.diff(rangeStart, 'hours').hours
  function makeAppointment({
    directionOverride,
    windowStartOverride,
  }: {
    directionOverride?: TransactionDirection
    windowStartOverride?: DateTime
  }) {
    const containerNumber = generateContainerNumber()
    const window_start =
      windowStartOverride ??
      rangeStart
        .plus({ hours: Math.floor(rand() * rangeHours) })
        .startOf('hour')
    let window_end: string | undefined
    if (rand() < 0.5) {
      window_end = window_start.plus({ hours: 1 }).toISO()
    }
    let directionForAppointment = directionOverride ?? direction
    if (!directionForAppointment) {
      directionForAppointment =
        rand() < 0.5
          ? TransactionDirection.Outbound
          : TransactionDirection.Inbound
    }
    const chanceLoaded =
      directionForAppointment === TransactionDirection.Outbound ? 0.8 : 0.2
    const daysToLFD = Math.floor(rand() * 9) - 4
    const lfd = window_start.plus({ days: daysToLFD })
    return createGETAppointmentWithContainer({
      appointment: createGETAppointment({
        id: appointmentID++,
        terminal_reference: `APPT-${appointmentID}`,
        container_number: containerNumber,
        window_start: window_start.toISO(),
        window_end,
        terminal: getRandomTerminal(terminals, rand),
        direction: directionForAppointment,
        loaded: loaded ?? rand() < chanceLoaded,
        status:
          statuses?.[Math.floor(rand() * statuses.length)] ??
          AppointmentStatus.Scheduled,
      }),
      watch: createGETWatch({
        container_number: containerNumber,
        import_last_free_date: lfd.toISO(),
      }),
    })
  }
  const appointments = Array.from({
    length: numAppointments,
  }).map(() => {
    return makeAppointment({})
  })
  // Add some dual appointments
  const dualAppointments = appointments
    .map((appointment) => {
      if (rand() < 0.5) {
        const directionOverride =
          appointment.appointment.direction === TransactionDirection.Outbound
            ? TransactionDirection.Inbound
            : TransactionDirection.Outbound
        const dualAppointment = makeAppointment({
          directionOverride,
          windowStartOverride: fastParseISO(
            appointment.appointment.window_start
          ),
        })
        dualAppointment.appointment.linked_appointments = [
          {
            terminal_reference: appointment.appointment.terminal_reference,
            container_number: appointment.appointment.container_number ?? '',
          },
        ]
        return dualAppointment
      }
      return undefined
    })
    .filter((appt) => appt !== undefined)
  return appointments.concat(dualAppointments)
}

export function createAppointmentWithContainerInfo(
  appointmentInfo?: Partial<GETAppointmentWithContainer>
): AppointmentWithContainerInfo {
  return new AppointmentWithContainerInfo(
    createGETAppointmentWithContainer(appointmentInfo),
    false
  )
}

export function createAppointmentWithContainerInfo_simple({
  containerNumber,
  terminalReference,
  containerType = ContainerType._40Hc,
  shippingLine = ShippingLine.Maeu,
  windowStart,
  windowEnd,
  direction = TransactionDirection.Outbound,
  loaded = true,
  status = AppointmentStatus.Scheduled,
  linkedAppointments,
  terminal,
}: {
  containerNumber: string
  terminalReference?: string
  containerType?: ContainerType
  shippingLine?: ShippingLine
  windowStart: DateTime
  windowEnd?: DateTime
  direction?: TransactionDirection
  loaded?: boolean
  status?: AppointmentStatus
  linkedAppointments?: LinkedAppointment[]
  terminal?: TerminalName
}) {
  return createAppointmentWithContainerInfo({
    appointment: createGETAppointment({
      container_number: containerNumber,
      terminal_reference: terminalReference,
      window_start: windowStart.toISO(),
      window_end: windowEnd?.toISO(),
      direction,
      loaded,
      status,
      linked_appointments: linkedAppointments,
      terminal,
    }),
    watch: createGETWatch({
      container_number: containerNumber,
      container_type: containerType,
      shipping_line: shippingLine,
      last_related_terminal: terminal,
    }),
  })
}

export function createGETWatch(watch?: Partial<GETWatch>): GETWatch {
  return {
    container_number: 'CBHU8733881',
    last_related_terminal: TerminalName.Apm,
    state: ContainerWatchState.ImportApptBooking,
    assigned_users: [],
    tags: [],
    port_complex: PortComplex.LaLb,
    ...watch,
  }
}

export function createGETPierPass(
  pierPass: Partial<GETPierPass> = {}
): GETPierPass {
  return {
    id: 1,
    terminals: [TerminalName.Apm],
    container_number: 'CBHU8733881',
    claimed_by_other_company: false,
    fee_paid: true,
    has_hold: false,
    observed: '2021-09-27T17:32:39.232453+00:00',
    last_observed: '2021-09-27T23:50:15.320766+00:00',
    ...pierPass,
  }
}

export function createGETContainer(
  container?: Partial<GETContainer>
): GETContainer {
  // Hapag-Lloyd rather than a line with special-case UI. Lines that withhold their
  // LFD from terminals (MSC/Maersk/OOCL/ONE) raise a CHECK LINE FOR LFD warning, and
  // COSCO raises a BOL-needed badge — as a default those would paint an unrelated
  // badge onto every story that just wants an ordinary container. Stories exercising
  // those paths pass the line explicitly.
  const importStatus = createGETImportStatus(
    container?.import_status ?? {
      shipping_line: 'HAP',
      parsed_shipping_line: ShippingLine.Hlcu,
      parsed_container_type: ContainerType._40Hc,
    }
  )
  return {
    container_number: 'CBHU8733881',
    import_statuses: [importStatus],
    assigned_users: [],
    auto_book_on: false,
    auto_book_ignoring_capacity: false,
    cycle_state: ContainerWatchState.ImportApptBooking,
    other_appointment_counts: [],
    import_status: importStatus,
    last_related_terminal: TerminalName.Apm,
    shipping_line: ShippingLine.Hlcu,
    container_type: ContainerType._40Hc,
    watched_time: DateTime.now().toISO(),
    tags: [],
    pier_pass: createGETPierPass(),
    clean_truck_fee: createGETPierPass(),
    port_complex: PortComplex.LaLb,
    ...container,
  }
}

export type ShippingLineFixture = Pick<
  GETShippingLine,
  'shipping_line' | 'short_desc' | 'long_desc' | 'url'
>

/**
 * Every state in which a missing line LFD is worth flagging, copied from what
 * `/containers/shipping_lines/` returns in production: the import lifecycle from
 * discharge onwards, on-vessel excluded.
 */
const LFD_CHECK_STATES = [
  ContainerWatchState.ImportTracking,
  ContainerWatchState.ImportApptBooking,
  ContainerWatchState.ImportApptBooked,
  ContainerWatchState.ImportPickedUp,
  ContainerWatchState.ImportEmpty,
  ContainerWatchState.ImportEmptyApptBooked,
  ContainerWatchState.ImportEmptyReturned,
]

/**
 * The four lines that withhold their LFD from terminals, with the notes and links
 * exactly as production serves them. Maersk's null URL is real, not an oversight:
 * its LFD sits behind a login, so there is no page to send anyone to.
 */
const LFD_GAPS: Partial<
  Record<ShippingLine, { lookupUrl: string | null; note: string }>
> = {
  MAEU: {
    lookupUrl: null,
    note:
      'Maersk does not share its line LFD with terminals \u2014 the date shown may not be' +
      ' your actual last free day. Check directly with Maersk.',
  },
  MSCU: {
    lookupUrl: 'https://www.msc.com/en/track-a-shipment',
    note:
      'MSC does not share its line LFD with terminals \u2014 the date shown may not be your' +
      ' actual last free day. Click to check on MSC.',
  },
  ONEY: {
    lookupUrl:
      'https://www.one-line.com/one-ecom/manage-shipment/cargo-tracking?sessLocale=en',
    note:
      'ONE does not share its line LFD with terminals \u2014 the date shown may not be your' +
      ' actual last free day. Click to check on ONE.',
  },
  OOLU: {
    lookupUrl:
      'https://www.oocl.com/eng/ourservices/eservices/cargotracking/Pages/cargotracking.aspx',
    note:
      'OOCL does not share its line LFD with terminals \u2014 the date shown may not be your' +
      ' actual last free day. Click to check on OOCL.',
  },
}

export function createGETShippingLine(
  line: ShippingLineFixture
): GETShippingLine {
  const gap = LFD_GAPS[line.shipping_line]
  return {
    ...line,
    shares_lfd_with_terminals: !gap,
    // Cast because the generator types these non-nullable while the spec marks them
    // nullable \u2014 production really does send null wherever there is no gap.
    lfd_lookup_url: gap?.lookupUrl ?? null,
    lfd_gap_note: gap?.note ?? null,
    lfd_check_states: gap ? LFD_CHECK_STATES : [],
  } as GETShippingLine
}

/**
 * The gap config as production serves it, so a fixture container on MSC/Maersk/OOCL/ONE
 * warns in tests and stories exactly as it does in the app.
 *
 * Built straight from `LFD_GAPS` rather than through `buildLineLFDGapConfig`, which is
 * deliberate: that function reports misconfigured lines through `reportWarning`, and
 * importing it here would drag Sentry into `test/setup.ts` (via the shipping-lines MSW
 * handler) before any test file can mock it — silently breaking `vi.mock('@sentry/vue')`
 * in unrelated suites. The builder has its own tests in `lineLFDGap.test.ts`.
 */
export const FIXTURE_LINE_LFD_GAP_CONFIG: LineLFDGapConfig = new Map(
  (
    Object.entries(LFD_GAPS) as [
      ShippingLine,
      (typeof LFD_GAPS)[ShippingLine],
    ][]
  )
    .filter(
      (
        entry
      ): entry is [ShippingLine, { lookupUrl: string | null; note: string }] =>
        Boolean(entry[1])
    )
    .map(([shipping_line, gap]) => [
      shipping_line,
      {
        note: gap.note,
        lookupUrl: gap.lookupUrl ?? undefined,
        checkStates: new Set(LFD_CHECK_STATES),
      },
    ])
)

export function createContainer(
  container?: Partial<GETContainer>,
  demoMode = false,
  lineLFDGapConfig: LineLFDGapConfig = FIXTURE_LINE_LFD_GAP_CONFIG
): Container {
  return new Container(
    createGETContainer(container),
    demoMode,
    lineLFDGapConfig
  )
}

export function createGETGateTransaction(
  transaction?: Partial<GateTransaction>
): GateTransaction {
  return {
    terminal: TerminalName.Apm,
    container_number: 'CBHU8733881',
    time: '2023-10-01T00:00:00.000Z',
    loaded: false,
    direction: TransactionDirection.Inbound,
    raw_data: {},
    observed: '2023-10-01T00:00:00.000Z',
    ...transaction,
  }
}

export function createSimpleUser(user?: Partial<SimpleUser>): SimpleUser {
  return {
    id: 1,
    first_name: 'John',
    last_name: 'Doe',
    ...user,
  }
}

export function createSavedCustomer(
  customer?: Partial<SavedCustomer>
): SavedCustomer {
  return {
    id: 1,
    name: 'Walmart',
    ...customer,
  }
}

export function createGETOrganization(
  org?: Partial<GETOrganization>
): GETOrganization {
  return {
    id: 1,
    name: 'Test Org',
    scac: 'TEST',
    config: {},
    role: UserRoleType.Management,
    subscriptions: [OrganizationSubscription.DrayDogMain],
    ...org,
  }
}

export function createGETUserInfo(
  userInfo?: Partial<GETUserInfo>
): GETUserInfo {
  const org = createGETOrganization(userInfo?.current_organization)
  const orgs = userInfo?.organizations ?? [org]
  return {
    id: 529,
    first_name: 'Nathaniel',
    last_name: 'Brown',
    email: 'nathaniel@draydog.com',
    preferences: {},
    current_organization: org,
    organizations: orgs,
    feature_flags: [],
    ...userInfo,
  }
}

export function createUserInfo(
  userInfo?: Partial<GETUserInfo>,
  demoMode = false
): UserInfo {
  return new UserInfo(createGETUserInfo(userInfo), demoMode)
}

export function createGETEmptyReturnRule(
  emptyReturnRule?: Partial<GETEmptyReturnRule>
): GETEmptyReturnRule {
  return {
    terminal: TerminalName.Apm,
    shipping_line: 'Maersk',
    parsed_shipping_line: ShippingLine.Maeu,
    container_type: '40HC',
    parsed_container_type: ContainerType._40Hc,
    rule: ReceivingRule.Open,
    group_last_observed: '2021-09-27T17:32:39.232453+00:00',
    group_first_observed: '2021-09-27T17:32:39.232453+00:00',
    reading_last_parsed: '2021-09-27T17:32:39.232453+00:00',
    screenshot_url: 'https://example.com/screenshot.png',
    ...emptyReturnRule,
  }
}

export function createEmptyReturnRule(
  emptyReturnRule?: Partial<GETEmptyReturnRule>
): ReturnRule {
  return new ReturnRule(createGETEmptyReturnRule(emptyReturnRule))
}
export function createGateScheduleEntry(
  entry?: Partial<GETGateScheduleEntryResponse>,
  first_observed?: DateTime,
  last_observed?: DateTime,
  screenshot_url?: string
): GateScheduleEntry {
  return new GateScheduleEntry(
    createGETGateScheduleEntryResponse(entry),
    first_observed ?? DateTime.now(),
    last_observed ?? DateTime.now(),
    screenshot_url ?? ''
  )
}
export function createGateScheduleEntryResponse(
  entry?: Partial<GETGateScheduleEntryResponse>
): GETGateScheduleEntryResponse {
  return createGETGateScheduleEntryResponse(entry)
}
export function createGETGateSchedule(
  schedule?: Partial<GETCurrentTerminalGateScheduleResponse>
): GETCurrentTerminalGateScheduleResponse {
  return {
    terminal: TerminalName.Apm,
    entries: [
      // APM
      createGETGateScheduleEntryResponse({
        date: '2024-03-12',
        shift: Shift.First,
        status: GateStatus.Open,
        notes: ['Automation only'],
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-13',
        shift: Shift.Second,
        status: GateStatus.Closed,
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-13',
        shift: Shift.Third,
        status: GateStatus.Closed,
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-14',
        shift: Shift.First,
        status: GateStatus.Open,
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-14',
        shift: Shift.First,
        status: GateStatus.Open,
        notes: ['Open for everyone'],
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-14',
        shift: Shift.Second,
        status: GateStatus.Closed,
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-16',
        shift: Shift.First,
        status: GateStatus.Closed,
        start_time: '2024-03-16T02:00:00.000Z', // After the end of the shift
        end_time: '2024-03-16T01:00:00.000Z',
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-16',
        shift: Shift.Second,
        status: GateStatus.Open,
      }),
      createGETGateScheduleEntryResponse({
        date: '2024-03-17',
        shift: Shift.First,
        status: GateStatus.Closed,
      }),
    ],
    screenshot_url: 'https://draydog.com/screenshot.png',
    first_observed: '2024-03-10T00:00:00.000Z',
    last_observed: '2024-03-10T01:00:00.000Z',
    last_parsed_time: '2024-03-10T01:00:00.000Z',
    ...schedule,
  }
}

export function createGETGateScheduleEntryResponse(
  entry?: Partial<GETGateScheduleEntryResponse>
): GETGateScheduleEntryResponse {
  return {
    date: '2024-03-10',
    shift: Shift.First,
    status: GateStatus.Open,
    notes: [],
    start_time: '2024-03-10T00:00:00.000Z',
    end_time: '2024-03-10T01:00:00.000Z',
    raw_period_description: '12:00 AM - 1:00 AM',
    ...entry,
  }
}

export function createTerminalGateSchedule(
  schedule?: Partial<GETCurrentTerminalGateScheduleResponse>
): TerminalGateSchedule {
  return new TerminalGateSchedule(createGETGateSchedule(schedule))
}

export function createGETContainersBasic(
  container?: Partial<GETContainerBasic>
): GETContainerBasic {
  return {
    container_number: 'CBHU8733881',
    last_related_terminal: TerminalName.Apm,
    cycle_state: ContainerWatchState.ImportApptBooking,
    tags: [],
    ...container,
  }
}

/**
 * Defaults are a real answer from /containers/line_and_type/ (MSKU6971689, the untracked
 * empty from the customer issue behind DEV-8461).
 */
export function createGETContainerLineAndType(
  data?: Partial<GETContainerLineAndType>
): GETContainerLineAndType {
  return {
    container_number: 'MSKU6971689',
    shipping_line: ShippingLine.Maeu,
    container_type: ContainerType._40Sd,
    last_seen: '2026-07-08T03:45:01.359000Z',
    ...data,
  }
}

// Module-level counter — accumulates across the whole vitest session, so
// auto-assigned ids are NOT stable across test order. Stories that need a
// specific id should pass it explicitly.
let nextCapacitySetId = 1

export function createGETCapacitySet(
  capacitySet?: Partial<GETCapacitySet>
): GETCapacitySet {
  const buckets: GETCapacityBucket[] = []
  for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
    // Create a bucket for each day at 0 hours (midnight) with 0 max_appointments
    buckets.push(
      createGETCapacityBucket({
        day_of_week: dayOfWeek,
        hour_of_day: 0,
        max_appointments: 0,
      })
    )
  }

  return {
    id: nextCapacitySetId++,
    last_edited: DateTime.now().toISO(),
    buckets,
    bucket_minutes: 1440,
    timezone: 'America/Los Angeles',
    port_complex: PortComplex.LaLb,
    ...capacitySet,
  }
}

export function createCapacitySet(
  capacitySet?: Partial<GETCapacitySet>
): CapacitySet {
  return makeCapacitySetFromAPI(createGETCapacitySet(capacitySet), false)
}

export function createGETCapacityBucket(
  bucket?: Partial<GETCapacityBucket>
): GETCapacityBucket {
  return {
    day_of_week: 0,
    hour_of_day: 0,
    max_appointments: 0,
    ...bucket,
  }
}

export function createEmptyInAppointmentSlot(
  slot?: Partial<GETEmptyInAppointmentSlot>,
  group_observed: string = '2024-03-10T00:00:00.000Z',
  shipping_line: ShippingLine = ShippingLine.Maeu
): EmptyInAppointmentSlot {
  return new EmptyInAppointmentSlot(
    {
      window_start: '2024-03-10T00:00:00.000Z',
      window_end: undefined,
      num_appointments_available: 3,
      ...slot,
    },
    group_observed,
    shipping_line
  )
}

export function createSlotDetails(details?: Partial<SlotDetails>): SlotDetails {
  return {
    recommended: undefined,
    warning: undefined,
    not_bookable: undefined,
    org_specific: undefined,
    ...details,
  }
}

export function createGETBaseAppointmentSlot(
  slot?: Partial<GETBaseAppointmentSlot>
): GETBaseAppointmentSlot {
  return {
    window_start: '2024-03-10T00:00:00.000Z',
    window_end: undefined,
    num_appointments_available: 2,
    warning: undefined,
    org_specific: false,
    details: createSlotDetails(),
    ...slot,
  }
}

export function createBaseAppointmentSlot(
  slot?: Partial<GETBaseAppointmentSlot>
): BaseAppointmentSlot {
  return new BaseAppointmentSlot(createGETBaseAppointmentSlot(slot))
}

export function createGETContainerAvailability(
  availability?: Partial<GETContainerAvailability>
): GETContainerAvailability {
  return {
    container_number: 'CBHU8733881',
    terminal: TerminalName.Apm,
    has_availability: true,
    slots: [],
    observed: '2024-03-10T00:00:00.000Z',
    ...availability,
  }
}
export function createAppointmentSlot(
  availability?: Partial<GETContainerAvailability>,
  slot?: Partial<GETBaseAppointmentSlot>
): AppointmentSlot {
  const parsedAvailability = createGETContainerAvailability(availability)
  const parsedSlot = createGETBaseAppointmentSlot(slot)
  return new AppointmentSlot(parsedAvailability, parsedSlot)
}

export function createGETTerminalReadingRules(
  terminalRulesObj?: Partial<TerminalReadingRules>
): TerminalReadingRules {
  return {
    terminal: TerminalName.Apm,
    reading_first_observed: '2021-01-01T00:00:00.000Z',
    reading_last_observed: '2021-01-01T00:00:00.000Z',
    reading_last_parsed: '2021-01-01T00:00:00.000Z',
    rules: [],
    cache_valid: true,
    ...terminalRulesObj,
  }
}

export function createGETVesselETAs(
  etas?: Partial<GETVesselSchedules>
): GETVesselSchedules {
  return {
    vessel_id: 1,
    // The schedule-row id of the primary eta below — the two are the same row, and
    // it's what the change-log endpoint's sibling fields are keyed against.
    id: 1,
    name: 'Maersk Mammoth',
    imo_number: 9928229, // Real IMO number so VesselFinder links work
    eta: {
      id: 1,
      eta: '2022-01-01T00:00:00Z',
      first_observed: '2022-01-01T00:00:00Z',
      portal_name: PortalName.VesselFinder,
      terminal: TerminalName.Apm,
    },
    other_etas: [],
    ...etas,
  }
}

/**
 * A vessel schedule change log as the API returns it: newest event first, ending
 * with the `eta_found` event for the first reading we ever took.
 *
 * `from_value`/`to_value` are cast because the generated client types them as
 * plain `string` while the API really sends `null` — see the note in
 * `models/vesselScheduleHistory.ts`. Fixtures have to be able to express the
 * nulls, otherwise they'd only ever exercise the happy path.
 */
export function createVesselScheduleHistory(
  history?: Partial<VesselScheduleHistory>
): VesselScheduleHistory {
  return {
    events: [
      {
        type: VesselScheduleEventType.ScheduleUpdated,
        time: '2026-07-26T16:15:00Z',
        source: PortalName.Everport,
        changes: [
          {
            field: VesselScheduleField.Erd,
            value_type: 'date',
            from_value: '2026-07-04',
            to_value: '2026-07-02',
          },
          {
            field: VesselScheduleField.CargoCutoff,
            value_type: 'datetime',
            from_value: '2026-07-03T17:00:00Z',
            to_value: '2026-07-01T17:00:00Z',
          },
        ],
      },
      {
        type: VesselScheduleEventType.EtaFound,
        time: '2026-07-20T13:10:00Z',
        source: PortalName.Everport,
        changes: [
          {
            field: VesselScheduleField.Eta,
            value_type: 'datetime',
            from_value: null,
            to_value: '2026-07-06T15:00:00Z',
          },
          {
            field: VesselScheduleField.Erd,
            value_type: 'date',
            from_value: null,
            to_value: '2026-07-04',
          },
        ],
      },
    ] as unknown as VesselScheduleHistory['events'],
    ...history,
  }
}

export function createCapacitySetRemainingCapacities(
  capacitySet?: Partial<CapacitySetRemainingCapacities>
): CapacitySetRemainingCapacities {
  return {
    capacity_set: {
      container_tag: undefined,
      independent_capacity: false,
      port_complex: PortComplex.LaLb,
      bucket_minutes: 60,
    },
    remaining_capacity_periods: [
      {
        capacity: 10,
        num_booked: 0,
        start: '2023-01-01T00:00:00Z',
        end: '2023-01-01T01:00:00Z',
      },
    ],
    ...capacitySet,
  }
}

export function createGETTruck(truck?: Partial<GETTruck>): GETTruck {
  return {
    id: 1,
    license_plate_number: 'ABCD123',
    name: "Jimmy's Truck",
    ...truck,
  }
}

export function createGETTruckWithStats(
  truckWithStats?: Partial<GETTruckWithStats>
): GETTruckWithStats {
  return {
    truck: createGETTruck(),
    num_completed: 0,
    num_scheduled: 0,
    num_missed: 0,
    ...truckWithStats,
  }
}

export function createGETBookingStyleRequestWithApptNum(
  bookingRequest?: Partial<GETBookingStyleRequestWithApptNum>
): GETBookingStyleRequestWithApptNum {
  return {
    window_start: '2023-01-01T00:00:00Z',
    entered_in_tms_time: '2023-01-01T01:00:00Z',
    auto_book_request_id: undefined,
    reschedule_cancellation_request_id: undefined,
    reschedule_request_id: undefined,
    status: OldBookingStatus.Booked,
    id: 1,
    terminal: TerminalName.Apm,
    container_number: 'ABCD1234567',
    requested_by: createSimpleUser(),
    created: '2023-01-01T00:00:00Z',
    action_type: 'book',
    appointment_terminal_reference: undefined,
    ...bookingRequest,
  }
}
export function createBookingRequest(
  bookingRequest?: Partial<GETBookingStyleRequestWithApptNum>
): BookingRequest {
  return new BookingRequest(
    createGETBookingStyleRequestWithApptNum(bookingRequest),
    false
  )
}

export function createGETBookingRequest(
  request?: Partial<GETBookingRequest>
): GETBookingRequest {
  return {
    id: 1,
    terminal: TerminalName.Apm,
    container_number: 'MAEU1234567',
    status: ActionStatus.Completed,
    created: DateTime.now().toISO(),
    organization_id: 1,
    truck_license_plate: '',
    connection_id: 1, // No way to get this sadly
    portal_name: PortalName.Apm,
    window_start: DateTime.now().plus({ days: 1 }).endOf('hour').toISO(),
    master_bill_of_lading: '',
    updated: DateTime.now().toISO(),
    action_type: 'book',
    ...request,
  }
}

export function createGETRescheduleRequest(
  request?: Partial<GETRescheduleRequest>
): GETRescheduleRequest {
  return {
    id: 1,
    terminal: TerminalName.Apm,
    container_number: 'MAEU1234567',
    status: ActionStatus.Completed,
    created: DateTime.now().toISO(),
    window_start: DateTime.now().plus({ days: 1 }).endOf('hour').toISO(),
    truck_license_plate: '',
    master_bill_of_lading: '',
    organization_id: 1,
    appointment_id: 1,
    connection_id: 1, // No way to get this sadly
    portal_name: PortalName.Apm,
    new_appointment_id: undefined,
    action_type: 'reschedule',
    ...request,
  }
}

export function createGETEmptyRescheduleRequest(
  request?: Partial<GETEmptyRescheduleRequest>
): GETEmptyRescheduleRequest {
  return {
    id: 1,
    terminal: TerminalName.Apm,
    container_number: 'MAEU1234567',
    status: ActionStatus.Completed,
    created: DateTime.now().toISO(),
    window_start: DateTime.now().plus({ days: 1 }).endOf('hour').toISO(),
    organization_id: 1,
    appointment_id: 1,
    connection_id: 1, // No way to get this sadly
    portal_name: PortalName.Apm,
    shipping_line: ShippingLine.Maeu,
    container_type: ContainerType._40Hc,
    new_appointment_id: undefined,
    action_type: 'reschedule_empty',
    ...request,
  }
}

export function createGETCancellationRequest(
  request?: Partial<GETCancellationRequest>
): GETCancellationRequest {
  return {
    id: 1,
    terminal: TerminalName.Apm,
    container_number: 'MAEU1234567',
    status: ActionStatus.Completed,
    created: DateTime.now().toISO(),
    organization_id: 1,
    appointment_id: 1,
    connection_id: 1, // No way to get this sadly
    portal_name: PortalName.Apm,
    action_type: 'cancel',
    ...request,
  }
}

export function createAutoBookRequestPreset(
  preset?: Partial<AutoBookRequestPreset>
): AutoBookRequestPreset {
  return {
    name: 'Test Preset',
    settings: {
      day_preference: DayPreferenceType.Earliest,
      min_advance_notice_hours: 1,
      timezone: 'America/Los_Angeles',
      time_preferences: [
        {
          start_local: '08:00',
          preference: PreferenceOption.High,
        },
      ],
    },
    ...preset,
  }
}

export function createAutoBookEmptySettings(
  settings?: Partial<AutoBookEmptySettings>
): AutoBookEmptySettings {
  return {
    day_preference: EmptyDayPreferenceType.Earliest,
    min_advance_notice_hours: 1,
    timezone: 'America/Los_Angeles',
    time_preferences: [
      {
        start_local: '08:00',
        preference: PreferenceOption.High,
      },
    ],
    eligible_container_states: [ContainerWatchState.ImportEmpty],
    ...settings,
  }
}

export function createGETAutoBookEmptyRequest(
  request?: Partial<GETAutoBookEmptyRequest>
): GETAutoBookEmptyRequest {
  return {
    id: 1,
    container_number: 'CBHU8733881',
    settings: createAutoBookEmptySettings(),
    status: AutoBookRequestStatus.Requested,
    set_by_user: createSimpleUser(),
    organization_id: 1,
    last_updated: DateTime.now().toISO(),
    ...request,
  }
}

export function createAutoBookEmptyRequestPreset(
  preset?: Partial<AutoBookEmptyRequestPreset>
): AutoBookEmptyRequestPreset {
  return {
    name: 'Test Empty Preset',
    settings: createAutoBookEmptySettings(),
    ...preset,
  }
}

export function createGETAutoBookRequest(
  request?: Partial<GETAutoBookRequest>
): GETAutoBookRequest {
  return {
    id: 1,
    container_number: 'CBHU8733881',
    enabled: true,
    settings: {
      day_preference: DayPreferenceType.Earliest,
      min_advance_notice_hours: 3,
      timezone: 'America/Los_Angeles',
      time_preferences: [
        {
          start_local: '06:00',
          preference: PreferenceOption.High,
        },
        {
          start_local: '12:00',
          preference: PreferenceOption.High,
        },
      ],
      custom_day_of_week_preferences: [],
      holds_to_block_booking: [],
      own_chassis: false,
      license_plate: '',
      master_bill_of_lading: '',
    },
    status: AutoBookRequestStatus.Requested,
    set_by_user: createSimpleUser(),
    organization_id: 1,
    last_updated: DateTime.now().toISO(),
    ...request,
  }
}

export function createGETAutoBookRequestMinimal(
  request?: Partial<GETAutoBookRequestMinimal>
): GETAutoBookRequestMinimal {
  return {
    id: 1,
    container_number: 'CBHU8733881',
    status: AutoBookRequestStatus.Requested,
    master_bill_of_lading: undefined,
    license_plate: undefined,
    chassis: undefined,
    set_random_license_plate: undefined,
    ...request,
  }
}

export function createGETSlotWindow(
  slot?: Partial<GETSlotWindow>
): GETSlotWindow {
  return {
    window_start: DateTime.now().plus({ hours: 2 }).toISO()!,
    window_end: DateTime.now().plus({ hours: 3 }).toISO()!,
    ...slot,
  }
}

export function createGETAutoBookSlotEvaluation(
  evaluation?: Partial<GETAutoBookSlotEvaluation>
): GETAutoBookSlotEvaluation {
  return {
    slot: createGETSlotWindow(evaluation?.slot),
    not_bookable_reasons: evaluation?.not_bookable_reasons ?? [],
    score: evaluation?.score,
  }
}

export function createGETAutoBookEvaluation(
  evaluation?: Partial<GETAutoBookEvaluation>
): GETAutoBookEvaluation {
  return {
    container_number: 'CBHU8733881',
    auto_book_request_id: 1,
    evaluation_time: DateTime.now().toISO()!,
    slots_reading_observed: DateTime.now().minus({ minutes: 5 }).toISO()!,
    terminal: TerminalName.Apm,
    container_not_bookable_reasons:
      evaluation?.container_not_bookable_reasons ?? [],
    slots: evaluation?.slots ?? [
      createGETAutoBookSlotEvaluation({
        slot: createGETSlotWindow({
          window_start: DateTime.now().plus({ hours: 2 }).toISO()!,
          window_end: DateTime.now().plus({ hours: 3 }).toISO()!,
        }),
        not_bookable_reasons: [],
      }),
    ],
    ...evaluation,
  }
}

export function createGETConnection(
  connection?: Partial<GETConnection>
): GETConnection {
  return {
    type: ConnectionType.ApmTermpoint,
    config: {
      user: 'test_user',
      password: 'test_password',
      client_id: 'test_client_id',
      client_secret: 'test_client_secret',
      api_key: 'test_api_key',
      tms_id: 'test_tms_id',
    },
    id: 1,
    validation_status: ConnectionValidationStatus.Validated,
    ...connection,
  }
}

export function createGETConnectionMetadata(
  metadata?: Partial<GETConnectionMetadata>
): GETConnectionMetadata {
  return {
    connection_type: ConnectionType.Emodal,
    connection_category: ConnectionCategory.AppointmentSystem,
    associated_portal: PortalName.Emodal,
    label: 'Emodal',
    url: 'https://www.emodal.com',
    region: [PortComplex.LaLb],
    is_dray_dog_managed: false,
    ...metadata,
  }
}

export function createGETToken(token?: Partial<GETToken>): GETToken {
  return {
    access_token: 'mock_token',
    token_type: 'bearer',
    user_id: 1,
    organization_id: 1,
    ...token,
  }
}

export function createGETCameraImage(
  image?: Partial<GETCameraImage>
): GETCameraImage {
  return {
    terminal: TerminalName.Apm,
    stream_name: 'gate_1',
    observed: DateTime.now().toISO(),
    image_url: 'https://example.com/camera-thumbnail.jpg',
    source_url: 'https://example.com/camera-source',
    ...image,
  }
}

export function createGETCameraHistoryImage(
  image?: Partial<GETCameraHistoryImage>
): GETCameraHistoryImage {
  const now = DateTime.now().toISO()
  return {
    terminal: TerminalName.Apm,
    stream_name: 'gate_1',
    observed: now,
    image_url: 'https://example.com/camera-thumbnail.jpg',
    source_url: 'https://example.com/camera-source',
    bucket_time: now,
    ...image,
  }
}

export function createGETActionMeta(
  action?: Partial<GETActionMetadata>
): GETActionMetadata {
  return {
    name: BotActionName.EditAppointmentAddEmpty,
    terminals: [TerminalName.Apm],
    arguments: {
      required: [
        'empty_container_number',
        'import_appointment',
        'shipping_line',
        'container_type',
      ],
      optional: ['chassis_number', 'truck_license_plate'],
      none: [],
    },
    ...action,
  }
}

let globalNotificationIdCounter = 1
export function createGlobalNotification(
  notification?: Partial<GlobalNotification>
): GlobalNotification {
  return {
    id: globalNotificationIdCounter++,
    level: GlobalNotificationLevelEnum.Info,
    title: 'Scheduled maintenance tonight',
    message: 'Dray Dog will be briefly unavailable at **11pm PST**.',
    display_type: GlobalNotificationDisplayType.Banner,
    active_from: '2024-01-01T20:00:00Z',
    active_to: undefined,
    ...notification,
  }
}

/**
 * The payload the backend documented for DEV-8313 (a cargo cutoff moved
 * earlier). Its `title` and `message` are built server-side and sent beside
 * it, in `Notification`, not inside this object.
 */
export function createExportDatesChangedNotification(
  notification?: Partial<ExportDatesChangedNotification>
): ExportDatesChangedNotification {
  return {
    schedule_id: 4821,
    vessel_name: 'MAERSK ESSEX',
    imo_number: 9778911,
    voyage: '0TW5GW1MA',
    terminal: TerminalName.Apm,
    changed_at: '2026-10-13T08:30:00Z',
    booking_numbers: ['NAM8436994B'],
    changes: [
      {
        field: VesselScheduleField.CargoCutoff,
        value_type: FieldChangeValueType.Datetime,
        from_value: '2026-10-16T17:00:00+00:00',
        to_value: '2026-10-16T12:00:00+00:00',
      },
    ],
    ...notification,
  }
}

let noteIdCounter = 1
export function createGETNote(note?: Partial<GETNote>): GETNote {
  return {
    id: noteIdCounter++,
    body: 'This container needs special handling at pickup.',
    time: '2024-03-15T14:30:00.000Z',
    user: createSimpleUser(),
    edited: false,
    ...note,
  }
}

export function createGETPortalMeta(
  portal?: Partial<GETPortalMetadata>
): GETPortalMetadata {
  return {
    portal_name: PortalName.Emodal,
    available_actions: {
      edit_appointment_add_empty: createGETActionMeta(),
    },
    ...portal,
  }
}

export function createGETExportStatus(
  status?: Partial<GETExportStatus>
): GETExportStatus {
  return {
    terminal: TerminalName.Apm,
    shipping_line: 'MAEU',
    vessel: 'MAERSK ANTARES',
    voyage: '129N',
    holds: [],
    equipment: [
      {
        container_type_code: '40DH',
        booked_qty: 5,
        issued_qty: 2,
        received_qty: 0,
        pick_remaining: 3,
        drop_remaining: 5,
        parsed_container_type: ContainerType._40Hc,
      },
    ],
    // Mirrored from the line's `pick_remaining` / `drop_remaining`, not derived
    // from booked/issued/received. The terminal's pregate reports both
    // remainders and neither is a function of the other counters — this line's
    // 5 booked / 2 issued / 3 left is one real shape among several, and a
    // fixture that recomputed it would only re-test the arithmetic we deleted.
    available_to_book_by_type: { [ContainerType._40Hc]: 3 },
    drop_available_by_type: { [ContainerType._40Hc]: 5 },
    observed: DateTime.now().minus({ hours: 1 }).toISO(),
    ...status,
    // The backend builds `available_to_book_by_type` by summing the equipment
    // lines (dd/models/export_statuses.py), so a real status never reports
    // availability for a type it has no line for. A fixture that overrides the
    // availability alone would otherwise keep the default 40HC line beside it —
    // data the API cannot produce, and invisible to anything that reads the
    // lines rather than the map. Explicit `equipment` always wins.
    ...(status?.available_to_book_by_type &&
      !status.equipment && {
        equipment: equipmentForAvailability(status.available_to_book_by_type),
        // The generated lines carry a pick count and nothing else, so the drop
        // map has to be emptied with them — leaving the default behind would
        // claim a drop remainder for types whose lines don't report one.
        drop_available_by_type: {},
      }),
  }
}

// One unissued line per type, booked exactly to its availability, its
// `pick_remaining` equal to the availability it was built from so the line and
// the map cannot disagree. The raw `container_type_code` is the enum value:
// fixtures only ever read the parsed type, and there is no reverse mapping to a
// terminal's own code to invent.
//
// To fixture a line the terminal has given us no count for, pass `equipment`
// explicitly with `pick_remaining` left off and keep the type out of the
// availability map — that is what an unrefreshed booking looks like.
function equipmentForAvailability(
  availability: GETExportStatus['available_to_book_by_type']
): ParsedExportEquipmentQuantity[] {
  return Object.entries(availability).map(([containerType, qty]) => ({
    container_type_code: containerType,
    parsed_container_type: containerType as ContainerType,
    booked_qty: qty,
    issued_qty: 0,
    received_qty: 0,
    pick_remaining: qty,
  }))
}

/**
 * A schedule row for a booking's resolved vessel at one terminal.
 *
 * Defaults line up with `createGETExportStatus` (same terminal, same voyage)
 * so a fixture booking's schedule actually joins to its status — a mismatched
 * voyage is dropped by `ExportBooking.scheduleByTerminal`, which would
 * otherwise make every schedule silently vanish from tests.
 */
export function createVesselScheduleForExport(
  schedule?: Partial<VesselScheduleForExport>
): VesselScheduleForExport {
  return {
    terminal: TerminalName.Apm,
    voyage_number: '129N',
    eta: DateTime.now().plus({ days: 8 }).toISO(),
    erd: DateTime.now().plus({ days: 2 }).toISODate(),
    cargo_cutoff: DateTime.now().plus({ days: 7 }).toISO(),
    documentation_cutoff: DateTime.now().plus({ days: 6 }).toISO(),
    reefer_cutoff: DateTime.now().plus({ days: 5 }).toISO(),
    hazmat_cutoff: DateTime.now().plus({ days: 4 }).toISO(),
    last_observed: DateTime.now().minus({ hours: 1 }).toISO(),
    // Nothing moved recently by default: a marker on every fixture would put
    // one on every existing story and test that never asked for it. Pass
    // `changed_at` with the matching `previous_*` to exercise one.
    ...schedule,
  }
}

export function createMatchedVesselForExport(
  vessel?: Partial<MatchedVesselForExport>
): MatchedVesselForExport {
  return {
    id: 4821,
    name: 'MAERSK ANTARES',
    imo_number: 9778815,
    ...vessel,
  }
}

/**
 * Defaults to a booking eModal found on the last check, which lines up with the
 * default `createGETExportStatus` (a tracked booking that has terminal data).
 * Pass `result` (and the timestamps) to fixture a `not_found`, `failing` or
 * `pending` booking.
 */
export function createGETExportLastCheck(
  lastCheck?: Partial<GETExportLastCheck>
): GETExportLastCheck {
  return {
    result: ExportBookingCheckStatus.Found,
    last_success_at: DateTime.now().minus({ minutes: 30 }).toISO(),
    last_attempt_at: DateTime.now().minus({ minutes: 30 }).toISO(),
    ...lastCheck,
  }
}

export function createGETExport(exportBooking?: Partial<GETExport>): GETExport {
  return {
    booking_number: generateBookingNumber(),
    state: ExportWatchState.Active,
    port_complex: PortComplex.LaLb,
    related_terminals: [TerminalName.Apm],
    shipping_line: 'MAEU',
    parsed_shipping_line: ShippingLine.Maeu,
    vessel: 'MAERSK ANTARES',
    voyage: '129N',
    added_by_user: createSimpleUser(),
    customer: createSavedCustomer(),
    statuses: [createGETExportStatus()],
    last_check: createGETExportLastCheck(),
    // Matches the default status, which reports availability and no holds.
    // Nothing in the frontend reads this yet (DEV-8135 builds the tabs on it);
    // it is required on the response, so a fixture has to state something, and
    // a value that contradicted the statuses below would be a trap later.
    lifecycle_state: ExportBookingState.Bookable,
    // `total_containers` is deliberately left unset: the backend derives it
    // from the booking's equipment lines, so a fixed default would contradict
    // any fixture that passes its own statuses — and the banner reads it, so
    // the tally would disagree with the rows right beneath it. Unset exercises
    // `ExportBooking.totalQty`'s fallback, which applies the same rule. Pass it
    // explicitly to fixture the server's own figure.
    ...exportBooking,
  }
}

/**
 * One row of `POST /exports/bookings/check/`, as the Add bookings table sees it.
 *
 * Defaults are the `found` case and line up with `createGETExportStatus` (same
 * terminal, same 40DH code) so a checked booking and a tracked one agree about
 * the same booking. `total_containers` is 5 to match that status's `booked_qty`
 * — the endpoint applies the list's TOTAL rule, so the two must not disagree.
 *
 * `message` and `total_containers` are nullable on the wire (no reason when
 * `found`; no count until a terminal reports equipment) but the generator drops
 * nullability off a required field, so the casts keep the fixture honest about
 * what the server actually sends.
 */
export function createGETExportBookingCheck(
  check?: Partial<GETExportBookingCheck>
): GETExportBookingCheck {
  return {
    booking_number: generateBookingNumber(),
    check_status: ExportBookingCheckStatus.Found,
    message: null as unknown as string,
    already_tracked: false,
    total_containers: 5,
    containers_by_type: [
      {
        container_type: ContainerType._40Hc,
        container_type_code: '40DH',
        booked_qty: 5,
      },
    ],
    terminals: [TerminalName.Apm],
    ...check,
  }
}
